import { SupabaseDB } from '@/utils/supabase';
import { message } from 'antd';

// Generic CRUD service using Supabase
export const createSupabaseService = <T = any>(tableName: string) => {
  return {
    // Get all records
    getAll: async (filters?: any) => {
      try {
        const { data, error } = await SupabaseDB.select<T>(tableName, '*', filters);
        
        if (error) {
          throw error;
        }

        return {
          success: true,
          data: data || [],
        };
      } catch (error: any) {
        message.error(error.message || `Failed to fetch ${tableName}`);
        return {
          success: false,
          message: error.message,
          data: [],
        };
      }
    },

    // Get by ID
    getById: async (id: any, idColumn = 'id') => {
      try {
        const { data, error } = await SupabaseDB.select<T>(tableName, '*', { [idColumn]: id });
        
        if (error) {
          throw error;
        }

        return {
          success: true,
          data: data?.[0] || null,
        };
      } catch (error: any) {
        message.error(error.message || `Failed to fetch ${tableName}`);
        return {
          success: false,
          message: error.message,
          data: null,
        };
      }
    },

    // Create record
    create: async (record: Partial<T>) => {
      try {
        const { data, error } = await SupabaseDB.insert<T>(tableName, record);
        
        if (error) {
          throw error;
        }

        message.success('Created successfully');
        return {
          success: true,
          data: data?.[0] || null,
        };
      } catch (error: any) {
        message.error(error.message || 'Create failed');
        return {
          success: false,
          message: error.message,
          data: null,
        };
      }
    },

    // Update record
    update: async (id: any, record: Partial<T>, idColumn = 'id') => {
      try {
        const { data, error } = await SupabaseDB.update<T>(tableName, id, record, idColumn);
        
        if (error) {
          throw error;
        }

        message.success('Updated successfully');
        return {
          success: true,
          data: data?.[0] || null,
        };
      } catch (error: any) {
        message.error(error.message || 'Update failed');
        return {
          success: false,
          message: error.message,
          data: null,
        };
      }
    },

    // Delete record
    delete: async (id: any, idColumn = 'id') => {
      try {
        const { error } = await SupabaseDB.delete(tableName, id, idColumn);
        
        if (error) {
          throw error;
        }

        message.success('Deleted successfully');
        return {
          success: true,
        };
      } catch (error: any) {
        message.error(error.message || 'Delete failed');
        return {
          success: false,
          message: error.message,
        };
      }
    },

    // Custom query
    query: () => {
      return SupabaseDB.customQuery(tableName);
    },
  };
};

// Specific services for your app
export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  created_at: string;
}

export interface Bike {
  id: string;
  name: string;
  status: string;
  location?: any;
  battery_level?: number;
  created_at: string;
}

export interface EVStation {
  id: string;
  name: string;
  location: any;
  status: string;
  created_at: string;
}

// User service
export const userService = createSupabaseService<User>('users');

// Bike service
export const bikeService = createSupabaseService<Bike>('bikes');

// EV Station service
export const evStationService = createSupabaseService<EVStation>('ev_stations');

export default {
  userService,
  bikeService,
  evStationService,
};
